import type { Anchor, BudgetPeriod } from "@rally/agreed";
import type { Reaction } from "./api-reactions.ts";
import type { TargetCapabilities } from "./api-targets.ts";
import type { AppOnMessage } from "./app-api.ts";

export type { Reaction, Reactor } from "./api-reactions.ts";
export type { TargetCapabilities } from "./api-targets.ts";

import type { Someone as Named } from "../chat/run/working.ts";

export type {
  ProfileResource,
  ReachableRepo,
  RunPage,
  RunState,
  RunStep,
  Served,
  SessionResource,
  SessionSummary,
  WorkspaceFile,
  WorkspaceSummary,
  WorkspaceTree,
} from "./api-resources.ts";

export type PutAway = {
  snoozedUntil: string | null;
  archivedForMe: boolean;
  archivedForAll: boolean;
  pinnedForMe: boolean;
  pinnedForAll: boolean;
};

export type RecordedStop = {
  kind: "stopped";
  id: string;

  by: string;

  agent: string | null;
  run: string;

  how: "refused" | "spent" | "broke" | "looped" | "vanished" | "tooled" | "halted";

  why: string;
  at: string;
};

export type RecordedCall = {
  kind: "call";
  id: string;
  state: CallState;
  startedAt: string | null;
  endedAt: string | null;
  people: number;
  summary: string | null;
  items: Array<{ id: string; for: string; text: string; doneAt: string | null }>;
  at: string;
};

export type Recorded = RecordedMessage | RecordedStop | RecordedCall;

export type Someone = Named;

export type NotHere = { handle: string; agent: string | null; displayName: string | null };

export type Busy = { thinking: Someone[]; typing: Someone[]; unanswered: Someone[]; notHere?: NotHere[] };

export type RecordedMessage = {
  kind: "message";
  id: string;
  by: string;
  text: string;
  source: "typed" | "spoken";
  call: string | null;
  at: string;
  revised: string | null;

  submission: string | null;
  between?: boolean;
  reactions: Reaction[];
  away: PutAway;
  answering: Answering | null;
  capabilities: TargetCapabilities;
  apps: AppOnMessage[];
};

export type Answering = { kind: TargetKind; id: string; said: string };

export type TargetKind = "conversation" | "message" | "call" | "file" | "app";

export type CallState = "upcoming" | "missed" | "live" | "ended";

export type CallMetadata = {
  call: string;
  conversation: string;
  state: CallState;
  spontaneous: boolean;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  host: { account: string; handle: string; displayName: string | null } | null;
};

export type PersonInCall = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;

  guest: boolean;
  joinedAt: string;
  leftAt: string | null;
  here: boolean;

  said: number;
};

export type CallInviteState = "live" | "full" | "revoked" | "expired";

export type ListedCallInvite = {
  state: CallInviteState;
  token: string | null;
  invitedBy: string | null;
  expiresAt: string;
  revokedAt: string | null;
  seats: number | null;
  admitted: number;
};

export type PersonInConversation = {
  account: string;
  handle: string;
  displayName: string | null;

  avatar: string | null;

  agent: boolean;
  role: string;

  removedAt: string | null;
};

export type PanelConversation = {
  id: string;
  title: string | null;

  description: string | null;
  mine: boolean;
  icon: string | null;

  pinnedAt: string | null;

  pinnedByMe: boolean;
};

export type WhereIAm = {
  me: {
    account: string;
    handle: string;
    displayName: string | null;
    avatar: string | null;
    role: string | null;
  };
  projects: Array<{
    id: string;
    name: string;

    description: string | null;

    icon: string | null;
    image: string | null;
    role: string;

    pinnedAt: string | null;

    pinnedByMe: boolean;
    conversations: PanelConversation[];
  }>;

  personal: PanelConversation[];
};

export type MyDevice = {
  credentialId: string;
  timesUsed: number;
  attachment?: string;
  transports?: string[];
  discoverable?: boolean;
  aaguid?: string;
  enrolledAt?: string;
};

export type Me = { account: string; handle: string; devices: MyDevice[] };

export type Preferences = {
  mode: string | null;
  palette: string | null;
  typeface: string | null;
  density: string | null;
  roundness: string | null;
  language: "auto" | "en" | "fr";
};

export type WhoInvited = { invitedBy: string | null; live: boolean };

export type CanJoin = { live: boolean };

export type ProjectContents = {
  conversations: Array<{ id: string; title: string | null; lastAt: string | null; mine: boolean }>;
  apps: Array<{ id: string; slug: string; name: string; description: string }>;
  repos: Array<{ name: string; url: string; deployHost: string | null }>;
  people: Array<{
    account: string;
    handle: string;
    avatar: string | null;
    role: string;

    removedAt: string | null;
  }>;
  ship: Array<{ repo: string; branch: string; target: string; gate: string | null }>;
};

export type ServerPerson = {
  id: string;
  handle: string;
  displayName: string | null;

  avatar: string | null;
  role: string | null;

  kind: "person" | "agent";
  removedAt: string | null;
};

export type ServerInvite = {
  state: "live" | "spent" | "revoked" | "expired";
  token: string | null;
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  redeemedBy: string | null;
  redeemedAt: string | null;
};

export type ListedFile = {
  id: string;
  name: string;
  bytes: number;
  mediaType: string;

  addedBy: string | null;

  message: string | null;
  at: string;
  comments: number;

  thumb: boolean;
  reactions: Reaction[];
};

export type FileComment = {
  id: string;
  by: string;
  text: string;
  at: string;
  anchor: Anchor | null;
  reactions: Reaction[];
  away: PutAway;

  answering: Answering | null;
  capabilities: TargetCapabilities;
};

export type ReadFile = {
  id: string;
  name: string;
  bytes: number;
  mediaType: string;
  addedBy: string | null;
  at: string;
  reactions: Reaction[];
  away: PutAway;
  comments: FileComment[];
  capabilities: TargetCapabilities;
};

export type TakenUpload = { key: string; bytes: number };

export type OpenedFile = { id: string; name: string; bytes: number; url: string };

export const BUDGET_SCOPES = ["project", "conversation", "participant"] as const;
export type BudgetScope = (typeof BUDGET_SCOPES)[number];

export type SpendBasis = "provider" | "estimate" | "unpriced";

export type CapHere = {
  scope: BudgetScope;
  id: string;
  period: BudgetPeriod;
  amount: number;
  spent: number;
};

export type SpendHere = {
  today: number;
  all: number;

  tokens: number;
  basis: SpendBasis;
  caps: CapHere[];
} | null;

export type SetBudget = CapHere & {
  name: string;

  removedAt: string | null;
};

export class Refused extends Error {
  constructor(
    readonly status: number,
    readonly which: string,
    message: string,
  ) {
    super(message);
  }
}

export function notSignedIn(cause: unknown): boolean {
  return cause instanceof Refused && cause.which === "NotSaidWhoYouAre";
}

export function withoutTheVerb(message: string): string {
  const said = message.replace(/^[a-z][\w.]*: /, "");
  return said.charAt(0).toUpperCase() + said.slice(1);
}

async function answered<T>(response: Response): Promise<T> {
  const said = await response.text();

  let body: unknown;
  let json = true;
  try {
    body = JSON.parse(said);
  } catch {
    json = false;
  }
  const named = body as { error?: string; message?: string } | null;
  if (!response.ok) {
    throw new Refused(
      response.status,
      named?.error ?? "Failed",
      withoutTheVerb(named?.message ?? `The server answered ${response.status} and said nothing.`),
    );
  }

  if (!json) {
    throw new Refused(response.status, "NotAnAnswer", "The server answered, and not with an answer.");
  }
  return body as T;
}

export async function read<T>(where: string, asks: Record<string, string> = {}): Promise<T> {
  const query = new URLSearchParams(asks).toString();
  return answered<T>(
    await fetch(`/r/${where}${query ? `?${query}` : ""}`, {
      credentials: "same-origin",
      cache: "no-store",
    }),
  );
}

export async function act<T>(what: string, body: unknown): Promise<T> {
  return answered<T>(
    await fetch(`/a/${what}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function upload<T>(
  what: string,
  asked: Record<string, string>,
  bytes: Blob,
  signal?: AbortSignal,
): Promise<T> {
  const query = new URLSearchParams(asked).toString();
  return answered<T>(
    await fetch(`/a/${what}${query ? `?${query}` : ""}`, {
      method: "POST",
      credentials: "same-origin",
      body: bytes,
      signal,
    }),
  );
}

export async function climbing<T>(
  what: string,
  asked: Record<string, string>,
  bytes: Blob,

  onSent: (sent: number) => void,

  onStop?: (stop: () => void) => void,
): Promise<T> {
  const query = new URLSearchParams(asked).toString();
  const asking = new XMLHttpRequest();
  onStop?.(() => asking.abort());
  const said = await new Promise<Response>((done, broke) => {
    asking.open("POST", `/a/${what}${query ? `?${query}` : ""}`);
    asking.withCredentials = true;
    asking.responseType = "text";
    asking.upload.onprogress = (event) => onSent(Math.min(event.loaded, bytes.size));
    asking.onload = () => {
      onSent(bytes.size);
      done(new Response(asking.responseText, { status: asking.status }));
    };
    asking.onerror = () => broke(new Refused(0, "NotReached", "The server could not be reached."));
    asking.onabort = () => broke(new Refused(0, "Stopped", "The upload was stopped."));
    asking.send(bytes);
  });
  return answered<T>(said);
}
